import { toast } from "$global-providers/toast-provider";
import { linkToInventoryMoveRequests } from "$routes/inventory/links";
import { gql } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import type { FulfillInventoryMoveRequestFulfillmentsInput } from "elephant-web/gql";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import {
  useFulfillInventoryMoveRequestMutation,
  useRejectInventoryMoveRequestMutation,
} from "./__generated__/use-fulfill-move-request-form.gql";
import type { FulfillmentSkuData } from "./queries";
import { validationSchema } from "./validation";

gql`
  mutation fulfillInventoryMoveRequest($input: FulfillInventoryMoveRequestInput!) {
    fulfillInventoryMoveRequest(input: $input) {
      id
    }
  }
`;

gql`
  mutation rejectInventoryMoveRequest($input: RejectInventoryMoveRequestInput!) {
    rejectInventoryMoveRequest(input: $input) {
      id
    }
  }
`;

type RequestSkuId = string;
type LotId = string;

export interface FormValues {
  skus: Record<
    RequestSkuId,
    Record<LotId, { quantity: string; quantityAvailable: number }>
  >;
  notes?: string;
}

interface Props {
  moveRequestId: string;
}

export function transformFormValuesToFulfillments(formValues: FormValues) {
  const fulfillments: FulfillInventoryMoveRequestFulfillmentsInput[] = [];

  Object.entries(formValues.skus || {}).forEach(([requestSkuId, lots]) => {
    Object.entries(lots).forEach(([lotId, { quantity }]) => {
      const quantityInt = parseInt(quantity, 10);
      if (quantityInt > 0) {
        fulfillments.push({
          requestSkuId,
          fulfillingLotId: lotId,
          quantity: parseInt(quantity, 10),
        });
      }
    });
  });

  return fulfillments;
}

export function transformDataToDefaultFormValues(
  fulfillmentSkusData: FulfillmentSkuData = [],
): FormValues {
  return {
    skus: fulfillmentSkusData.reduce(
      (acc, sku) => ({
        ...acc,
        [sku.id]: sku.availableLots.reduce(
          (lotAcc, lot) => ({
            ...lotAcc,
            [lot.id]: { quantityAvailable: lot.quantity },
          }),
          {},
        ),
      }),
      {},
    ),
  };
}

export default function useFulfillMoveRequestForm({ moveRequestId }: Props) {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();

  const formMethods = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    mode: "onTouched",
  });

  const [fulfillRequest, { loading }] = useFulfillInventoryMoveRequestMutation({
    onError: () =>
      toast.error(
        formatMessage({
          id: "inventory.moveRequest.fulfill.error",
          defaultMessage: "Error Fulfilling Request",
        }),
      ),
    onCompleted: () => {
      toast.success(
        formatMessage({
          id: "inventory.moveRequest.fulfill.success",
          defaultMessage: "Fulfilled Request",
        }),
      );

      navigate(linkToInventoryMoveRequests());
    },
  });

  async function submit(formValues: FormValues) {
    await fulfillRequest({
      variables: {
        input: {
          id: moveRequestId,
          fulfillments: transformFormValuesToFulfillments(formValues),
          notes: formValues.notes,
        },
      },
    });
  }

  const [rejectRequest] = useRejectInventoryMoveRequestMutation({
    onError: () =>
      toast.error(
        formatMessage({
          id: "inventory.moveRequest.reject.error",
          defaultMessage: "Error Rejecting Request",
        }),
      ),
    onCompleted: () => {
      toast.success(
        formatMessage({
          id: "inventory.moveRequest.reject.success",
          defaultMessage: "Rejected Request",
        }),
      );
      navigate(linkToInventoryMoveRequests());
    },
  });

  const notes = formMethods.watch("notes");
  const skus = formMethods.watch("skus");

  const disableSubmit =
    transformFormValuesToFulfillments({ skus }).length === 0;

  async function reject() {
    if (!notes) {
      formMethods.setError(
        "notes",
        {
          type: "focus",
          message: formatMessage({
            id: "inventory.moveRequest.reject.notesValidationError",
            defaultMessage: "Required",
          }),
        },
        { shouldFocus: true },
      );
      return;
    }

    await rejectRequest({
      variables: {
        input: {
          id: moveRequestId,
          notes,
        },
      },
    });
  }

  return {
    formMethods,
    onSubmit: formMethods.handleSubmit(submit),
    onReject: reject,
    resetSkuValues: (data: FulfillmentSkuData = []) =>
      formMethods.reset(transformDataToDefaultFormValues(data)),
    disableSubmit,
    loading,
  };
}
